import { useEffect, useState } from "react"
import { Container, Row, Col, Alert } from "react-bootstrap"
import { useTranslation } from "react-i18next"
import { useNavigate, useParams, useSearchParams, useLocation } from "react-router-dom"
import GuestInfo from "./components/GuestInfo"
import { getInvitationByHash, GuestInvitation } from "@/api/scanner"

const GuestCheckInPage = () => {
  const { t } = useTranslation()
  const { hash } = useParams<{ hash: string }>()
  const [searchParams] = useSearchParams()
  const location = useLocation()
  const navigate = useNavigate()
  const [guest, setGuest] = useState<GuestInvitation | null>(null)
  const [error, setError] = useState<string>("")
  const [loading, setLoading] = useState(true)
  const eventId = searchParams.get("event_id")
  
  // Check if guest data was passed via navigation state
  const passedGuestData = location.state?.guestData as GuestInvitation | undefined

  useEffect(() => {
    if (!hash || !eventId) {
      navigate("/scanner")
      return
    }

    // If we have guest data from navigation state, use it directly (no fetch needed)
    if (passedGuestData) {
      console.log("✅ Using guest data from navigation state (already validated)")
      setGuest(passedGuestData)
      setLoading(false)
      return
    }

    // Otherwise, fetch from API (e.g., page refresh or direct URL access)
    const fetchGuest = async () => {
      try {
        console.log("🔍 Fetching invitation for hash:", hash, "event_id:", eventId)
        const invitation = await getInvitationByHash(hash, eventId)
        console.log("✅ Invitation found:", invitation)
        setGuest(invitation)
      } catch (err: any) {
        console.error("❌ Error fetching invitation:", err)
        // Redirect to invalid QR page for any error
        navigate("/scanner/invalid")
      }
    }

    fetchGuest()
  }, [hash, eventId, passedGuestData, navigate, t])

  const handleGuestUpdate = (updatedGuest: GuestInvitation) => {
    setGuest(updatedGuest)
  }

  const refetchGuest = async () => {
    if (!hash || !eventId) return
    
    try {
      console.log("🔄 Re-fetching guest data after check-in/out")
      const invitation = await getInvitationByHash(hash, eventId)
      setGuest(invitation)
      console.log("✅ Guest data refreshed:", invitation)
    } catch (err: any) {
      console.error("❌ Error refreshing guest data:", err)
    }
  }

  const handleScanAgain = () => {
    navigate("/scanner")
  }

  return (
    <Container className="py-4" style={{ maxWidth: "800px" }}>
      <Row className="mb-4">
        <Col>
          <div className="text-center">
            <h1 className="h2 mb-0">{t("scanner.guestCheckIn")}</h1>
          </div>
        </Col>
      </Row>

      {loading && (
        <Row className="mb-4">
          <Col>
            <Alert variant="info">
              {t("scanner.loadingInvitation")}
            </Alert>
          </Col>
        </Row>
      )}

      {error && (
        <Row className="mb-4">
          <Col>
            <Alert variant="danger">
              {error}
            </Alert>
          </Col>
        </Row>
      )}

      {guest && (
        <Row className="mb-4">
          <Col>
            <GuestInfo 
              guest={guest} 
              onUpdate={handleGuestUpdate} 
              onRefetch={refetchGuest}
              onScanAgain={handleScanAgain}
            />
          </Col>
        </Row>
      )}
    </Container>
  )
}

export default GuestCheckInPage

